import React, { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { PageHeader } from '../components/ui/PageHeader';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { FormField } from '../components/ui/FormField';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

interface Organization {
  id: number;
  name: string;
  description: string;
  website_url?: string;
  api_documentation_url?: string;
  contact_email?: string;
  api_key_required?: boolean;
  rate_limit_info?: string;
  pricing_info?: string;
  status: 'active' | 'inactive' | 'testing' | 'deprecated';
  created_at: string;
  updated_at: string;
  api_count?: number;
}

interface EditModalProps {
  organization: Organization | null;
  onSave: (data: Partial<Organization>) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
}

const EditModal: React.FC<EditModalProps> = ({ organization, onSave, onClose, isOpen }) => {
  const [formData, setFormData] = useState<Partial<Organization>>({
    name: '',
    description: '',
    website_url: '',
    api_documentation_url: '',
    contact_email: '',
    api_key_required: true,
    rate_limit_info: '',
    pricing_info: '',
    status: 'active'
  });

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        description: organization.description || '',
        website_url: organization.website_url || '',
        api_documentation_url: organization.api_documentation_url || '',
        contact_email: organization.contact_email || '',
        api_key_required: organization.api_key_required ?? true,
        rate_limit_info: organization.rate_limit_info || '',
        pricing_info: organization.pricing_info || '',
        status: organization.status || 'active'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        website_url: '',
        api_documentation_url: '',
        contact_email: '',
        api_key_required: true,
        rate_limit_info: '',
        pricing_info: '',
        status: 'active'
      });
    }
  }, [organization]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const errors: string[] = [];
    
    if (!formData.name?.trim()) {
      errors.push('Name is required');
    }
    
    if (!formData.description?.trim()) {
      errors.push('Description is required');
    }
    
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }
    
    await onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 w-full max-w-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {organization ? 'Edit Organization' : 'Create New Organization'}
            </h2>
            <Button variant="ghost" onClick={onClose}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="Name" required>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </FormField>

            <FormField label="Website URL">
              <input
                type="url"
                value={formData.website_url}
                onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="https://example.com"
              />
            </FormField>

            <FormField label="API Documentation URL">
              <input
                type="url"
                value={formData.api_documentation_url}
                onChange={(e) => setFormData({...formData, api_documentation_url: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="https://docs.example.com"
              />
            </FormField>

            <FormField label="Contact Email">
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="contact@example.com"
              />
            </FormField>

            <FormField label="API Key Required">
              <select
                value={formData.api_key_required ? 'true' : 'false'}
                onChange={(e) => setFormData({...formData, api_key_required: e.target.value === 'true'})}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </FormField>

            <FormField label="Status">
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="testing">Testing</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </FormField>
          </div>

          <FormField label="Description" required>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              placeholder="Describe the organization..."
              required
            />
          </FormField>

          <FormField label="Rate Limit Info">
            <textarea
              value={formData.rate_limit_info}
              onChange={(e) => setFormData({...formData, rate_limit_info: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows={2}
              placeholder="Rate limiting information..."
            />
          </FormField>

          <FormField label="Pricing Info">
            <textarea
              value={formData.pricing_info}
              onChange={(e) => setFormData({...formData, pricing_info: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows={2}
              placeholder="Pricing structure information..."
            />
          </FormField>
        </form>
        
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {organization ? 'Update Organization' : 'Create Organization'}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={onConfirm}>
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminOrganizationsPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getOrganizations();
      setOrganizations(data);
    } catch (e: any) {
      console.error('Error fetching organizations:', e);
      setError(e.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedOrganization(null);
    setShowEditModal(true);
  };

  const handleEdit = (organization: Organization) => {
    setSelectedOrganization(organization);
    setShowEditModal(true);
  };

  const handleSave = async (data: Partial<Organization>) => {
    try {
      if (selectedOrganization) {
        await apiService.updateOrganization(selectedOrganization.id, data);
      } else {
        await apiService.createOrganization(data);
      }
      await fetchOrganizations();
    } catch (e: any) {
      console.error('Error saving organization:', e);
      setError(e.message || 'Failed to save organization');
    }
  };

  const handleDelete = (organization: Organization) => {
    setSelectedOrganization(organization);
    setConfirmAction(() => async () => {
      try {
        await apiService.deleteOrganization(organization.id);
        await fetchOrganizations();
      } catch (e: any) {
        setError(e.message || 'Failed to delete organization');
      }
      setShowConfirmModal(false);
    });
    setShowConfirmModal(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedOrganizations = () => {
    return [...organizations].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        default:
          aValue = a.name || '';
          bValue = b.name || '';
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <PageHeader
        title="Organization Management"
        align="center"
        actionButtonText="+ New Organization"
        onActionButtonClick={handleCreate}
      />
      
      {error && (
        <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-300 rounded">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-8">
          <LoadingSpinner text="Loading organizations..." />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Mobile Card View */}
          <div className="block lg:hidden">
            <div className="grid grid-cols-1 gap-4">
              {getSortedOrganizations().map(org => (
                <Card key={org.id} className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{org.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 break-words whitespace-pre-line">{org.description}</p>
                      </div>
                      <Badge variant={org.status === 'active' ? 'success' : org.status === 'inactive' ? 'danger' : 'warning'} className="ml-2 flex-shrink-0">
                        {org.status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      {org.website_url && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-200">Website:</span>
                          <a href={org.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 ml-2">
                            {org.website_url}
                          </a>
                        </div>
                      )}
                      {org.api_documentation_url && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-200">API Docs:</span>
                          <a href={org.api_documentation_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 ml-2">
                            {org.api_documentation_url}
                          </a>
                        </div>
                      )}
                      {org.contact_email && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-200">Email:</span>
                          <span className="text-gray-900 dark:text-white ml-2">{org.contact_email}</span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-200">API Key Required:</span>
                        <span className="text-gray-900 dark:text-white ml-2">{org.api_key_required ? 'Yes' : 'No'}</span>
                      </div>
                      {org.api_count !== undefined && (
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-200">APIs:</span>
                          <span className="text-gray-900 dark:text-white ml-2">{org.api_count}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(org)}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDelete(org)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <Card className="overflow-hidden">
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Name</span>
                          {getSortIcon('name')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact</th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {getSortIcon('status')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">APIs</th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Created</span>
                          {getSortIcon('created_at')}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {getSortedOrganizations().map(org => (
                      <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{org.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs" title={org.description}>
                            {org.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          <div className="space-y-1">
                            {org.contact_email && (
                              <div className="text-blue-600 dark:text-blue-400">{org.contact_email}</div>
                            )}
                            {org.website_url && (
                              <div>
                                <a href={org.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 text-xs">
                                  {org.website_url}
                                </a>
                              </div>
                            )}
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              API Key: {org.api_key_required ? 'Required' : 'Not Required'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={org.status === 'active' ? 'success' : org.status === 'inactive' ? 'danger' : 'warning'}>
                            {org.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                          {org.api_count || 0}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {new Date(org.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium">
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm" onClick={() => handleEdit(org)}>
                              Edit
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => handleDelete(org)}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {organizations.length === 0 && !loading && (
            <Card className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No organizations found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating your first organization.</p>
              <div className="mt-6">
                <Button variant="primary" onClick={handleCreate}>
                  Create Organization
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      <EditModal
        organization={selectedOrganization}
        onSave={handleSave}
        onClose={() => setShowEditModal(false)}
        isOpen={showEditModal}
      />

      <ConfirmModal
        isOpen={showConfirmModal}
        title="Delete Organization"
        message={`Are you sure you want to delete "${selectedOrganization?.name}"? This action cannot be undone.`}
        onConfirm={() => confirmAction?.()}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
};

export default AdminOrganizationsPage;